import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Send, Save, Plus, Trash2, Copy, ArrowUp, ArrowDown,
    Mail as MailIcon, Pencil, X,
} from "lucide-react";
import * as api from "../../lib/crmApi";
import { sanitizeHtml } from "../../lib/sanitize";
import RichTextEditor from "../../components/RichTextEditor";

const TOKENS = [
    "kontaktperson", "virksomhedsnavn", "fornavn",
    "ansvarlig_sælger", "sælger_telefon", "sælger_email", "booking_link",
];

/**
 * Mail-skabelon-editor. Erstatter det gamle automation-flow — reps
 * vælger nu manuelt en skabelon fra Kanban-kortet/drawer og trykker
 * send. Ingen tidsforsinkelse, ingen trigger-regler.
 */
export default function CrmTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testEmail, setTestEmail] = useState("");
    const [editing, setEditing] = useState(null); // template object or "__new__"
    const [previewId, setPreviewId] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const t = await api.listTemplates();
            setTemplates(t);
        } catch {
            toast.error("Kunne ikke hente skabeloner");
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); }, []);

    const move = async (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= templates.length) return;
        const copy = [...templates];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        setTemplates(copy);
        try { await api.reorderTemplates(copy.map((t) => t.id)); }
        catch { toast.error("Kunne ikke gemme rækkefølge"); refresh(); }
    };

    const duplicate = async (t) => {
        try {
            await api.createTemplate({
                name: `${t.name} (kopi)`,
                subject: t.subject,
                html: t.html,
            });
            toast.success("Skabelon kopieret");
            refresh();
        } catch { toast.error("Fejl ved kopi"); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Slet skabelonen "${t.name}"?`)) return;
        try {
            await api.deleteTemplate(t.id);
            toast.success("Slettet");
            refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    };

    const sendTest = async (t) => {
        if (!testEmail || !testEmail.includes("@")) {
            return toast.error("Indtast test-email øverst");
        }
        try {
            await api.sendTestTemplate(t.id, testEmail);
            toast.success(`Testmail sendt til ${testEmail}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fejl");
        }
    };

    if (loading) return (
        <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
    );

    return (
        <div className="p-6 lg:p-8" data-testid="crm-templates">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900">Mailskabeloner</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Gem dine standard-mails og send dem manuelt fra lead-kortet
                    </p>
                </div>
                <button
                    onClick={() => setEditing("__new__")}
                    data-testid="crm-template-new"
                    className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10">
                    <Plus className="h-4 w-4" /> Ny skabelon
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Send test-mails til
                </label>
                <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="fx dig@dinemail.dk"
                    data-testid="crm-test-email"
                    className="w-full max-w-md rounded-lg border border-slate-300 h-10 px-3 text-sm" />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
                <div className="text-xs font-semibold text-slate-700 mb-2">
                    Dynamiske felter — klik for at kopiere, indsæt derefter i emne eller indhold
                </div>
                <div className="flex flex-wrap gap-2">
                    {TOKENS.map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => {
                                const token = `{{${t}}}`;
                                navigator.clipboard?.writeText(token).catch(() => {});
                                toast.success(`Kopieret: ${token}`);
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sky-50 border border-sky-200 rounded-full text-xs font-mono text-sky-700 hover:bg-sky-100 hover:border-sky-300 transition-colors">
                            {`{{${t}}}`}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-3">
                {templates.map((t, i) => (
                    <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4"
                         data-testid={`crm-template-row-${t.id}`}>
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
                                <MailIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-lg font-bold text-slate-900 truncate">{t.name}</div>
                                <div className="text-sm text-slate-500 truncate mt-0.5">
                                    <span className="font-mono text-xs text-slate-400">Emne:</span> {t.subject}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => setPreviewId(t.id)} title="Forhåndsvis"
                                    className="rounded-lg border border-slate-300 px-2.5 h-9 text-xs hover:bg-slate-50">
                                    Forhåndsvis
                                </button>
                                <button onClick={() => sendTest(t)}
                                    data-testid={`crm-template-test-${t.id}`}
                                    className="rounded-lg bg-slate-900 text-white px-2.5 h-9 text-xs font-semibold inline-flex items-center gap-1">
                                    <Send className="h-3 w-3" /> Test
                                </button>
                                <button onClick={() => setEditing(t)}
                                    data-testid={`crm-template-edit-${t.id}`}
                                    className="rounded-lg border border-sky-300 text-sky-700 px-2.5 h-9 text-xs hover:bg-sky-50 inline-flex items-center gap-1">
                                    <Pencil className="h-3 w-3" /> Rediger
                                </button>
                                <button onClick={() => duplicate(t)} title="Kopiér"
                                    className="rounded-lg border border-slate-300 px-2.5 h-9 text-xs hover:bg-slate-50">
                                    <Copy className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => move(i, -1)} disabled={i === 0} title="Op"
                                    className="rounded-lg border border-slate-300 px-2.5 h-9 text-xs disabled:opacity-30">
                                    <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => move(i, 1)} disabled={i === templates.length - 1} title="Ned"
                                    className="rounded-lg border border-slate-300 px-2.5 h-9 text-xs disabled:opacity-30">
                                    <ArrowDown className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => remove(t)} title="Slet"
                                    className="rounded-lg border border-rose-300 text-rose-600 px-2.5 h-9 text-xs hover:bg-rose-50">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {templates.length === 0 && (
                    <div className="text-center py-12 text-slate-400 text-sm">
                        Ingen skabeloner endnu — klik "Ny skabelon" for at komme i gang.
                    </div>
                )}
            </div>

            {editing && (
                <TemplateEditor
                    template={editing === "__new__" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}
            {previewId && (
                <PreviewModal
                    template={templates.find((t) => t.id === previewId)}
                    onClose={() => setPreviewId(null)}
                />
            )}
        </div>
    );
}

function TemplateEditor({ template, onClose, onSaved }) {
    const isNew = !template;
    const [name, setName] = useState(template?.name || "");
    const [subject, setSubject] = useState(template?.subject || "");
    const [html, setHtml] = useState(template?.html || "<p></p>");
    const [saving, setSaving] = useState(false);

    const valid = useMemo(
        () => name.trim() && subject.trim() && html.trim() && html !== "<p></p>",
        [name, subject, html],
    );

    const save = async () => {
        if (!valid) { toast.error("Udfyld alle felter"); return; }
        setSaving(true);
        try {
            if (isNew) await api.createTemplate({ name, subject, html });
            else await api.updateTemplate(template.id, { name, subject, html });
            toast.success(isNew ? "Skabelon oprettet" : "Skabelon opdateret");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fejl");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="crm-template-editor">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl font-bold text-slate-900">
                        {isNew ? "Ny skabelon" : "Rediger skabelon"}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Navn</label>
                        <input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="fx Mail 1 · Intro"
                            data-testid="crm-template-name"
                            className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Emnelinje</label>
                        <input value={subject} onChange={(e) => setSubject(e.target.value)}
                            placeholder="Hej {{fornavn}} …"
                            data-testid="crm-template-subject"
                            className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Indhold</label>
                        <RichTextEditor value={html} onChange={setHtml} testid="crm-template-html" />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose}
                        className="px-4 h-10 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
                        Annuller
                    </button>
                    <button onClick={save} disabled={saving || !valid}
                        data-testid="crm-template-save"
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 disabled:opacity-50">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {isNew ? "Opret" : "Gem"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PreviewModal({ template, onClose }) {
    if (!template) return null;
    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
                 onClick={(e) => e.stopPropagation()}>
                <div className="text-xs text-slate-500 mb-1">Emnelinje</div>
                <div className="font-semibold text-lg text-slate-900 mb-4">{template.subject}</div>
                <div className="text-xs text-slate-500 mb-1">Indhold</div>
                <div className="border border-slate-200 rounded-lg p-4 prose prose-sm max-w-none"
                     dangerouslySetInnerHTML={{ __html: sanitizeHtml(template.html) }} />
                <div className="mt-4 flex justify-end">
                    <button onClick={onClose}
                        className="rounded-lg bg-slate-900 text-white px-4 h-10 text-sm font-semibold">
                        Luk
                    </button>
                </div>
            </div>
        </div>
    );
}
